import React, { useMemo, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectLanguage } from '../../redux/selectors';
import type { Place } from '../../types/place';
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import { getPlace } from '../../services/data';
import ButtonsGrid from '../../components/common/ButtonsGrid';
import AccessibilityModal from '../../components/common/modals/AccessibilityModal';
import {configureWidgets} from '../../components/widgets/widgetsOptions'

import styles from '../../components/common/modals/Modal.module.css'
import ImagesCarousel from '../../components/common/ImagesCarousel';
import WidgetManager from '../../components/widgets/WidgetManager';
import { useAsyncData } from '../../hooks/useAsyncData';

interface StationPageProps {
    placeId: string;
}

const StationPage: React.FC<StationPageProps> = ({placeId}) => {
    const language = useAppSelector(selectLanguage)

    const [showImg, setShowImg] = useState<boolean>(true)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const [asset, setAsset] = useState<string>('');
    const [selectedWidget, setSelectedWidget] = useState<string>('text')

    const { data: station, isLoading, error } = useAsyncData<Place | null>(
        () => placeId ? getPlace(placeId, language.currentLanguage ?? 'en') : Promise.resolve(null),
        [language, placeId]
    );

    const options = useMemo(
        () => station ? configureWidgets(station.translations[0]) : [],
        [station]
    );


    const handleOption = (option: string) => {
        setAsset(option)
        setIsModalOpen(false)
    };

    const handleWidgetSelect = (widget: string) => {
        setSelectedWidget(widget)
        setShowImg(!(widget === 'chat' || widget.includes('audio') || widget.includes('video')))
    }


    if (error) {
        return (
            <Box sx={{ direction: language.direction, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                <Typography variant='h2' sx={{ mb: 2 }}>⚠</Typography>
                <Typography variant='body1' sx={{ textAlign: 'center' }}>{error}</Typography>
            </Box>
        );
    }

    return (
        <Box className='galleries-page page' sx={{direction: language.direction, display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem'}}>
            <Typography variant='h1'>
                {station?.translations[0].information_title}
            </Typography>
            {
                station?.translations[0].information_subtitle  && 
                <Typography variant='body1' sx={{ marginTop: '0.5rem', marginBottom: '1rem'}}>
                    {station?.translations[0].information_subtitle}
                </Typography>
            }

            {
                showImg && 
                <>
                    <Box sx={{
                        overflowX: 'auto',
                        overflowY: 'clip',
                        '&::-webkit-scrollbar': {
                            height: '0'
                        },
                        }}
                    >
                        {/* {station && <img src={createImageUrl(station?.information_photo)} onError={addDefaultSource} onLoad={handleImgLoad}/>} */}
                        <ImagesCarousel images={station?.information_photos} />
                    </Box> 
                    <Divider sx={{borderColor: 'white', mb: '1.5rem'}}/>
                </>
            }

        
            <Box sx={{flex: 1, overflowY: 'auto', direction: 'ltr'}}>
                {station && 
                    <WidgetManager 
                        currentAsset={asset}
                        translations={station.translations[0]}
                        widget={selectedWidget}
                        stationName={station.translations[0].information_title}
                    />
                }
            </Box>

            <Box className='widgets-options'
                sx={{
                    //  display: 'grid',
                    marginTop: '3rem',
                    paddingBottom: '1rem',
                    direction: 'ltr',
                    gridTemplateColumns: {
                        xs: 'repeat(3, minmax(0, 1fr))',
                        sm: 'auto repeat(3, minmax(0, 1fr))'
                    },
                    gap: 1,
                    justifyItems: 'center',
                    alignItems: 'center',
                    mt: '1.5rem'
                }}
            >
                <ButtonsGrid options={options} setOption={setAsset} toggleModal={setIsModalOpen} onWidgetSelect={handleWidgetSelect} currentWidget={selectedWidget}/>
            </Box>
            
            <div className={styles['modal-backdrop']} onClick={() => setIsModalOpen(false)} style={{display: !isModalOpen? 'none' : ''}}>

            <div className={styles['modal-container']} onBlur={()=>setIsModalOpen(false)} >
                <AccessibilityModal setAsset={handleOption}/>
            </div>
            </div>

        </Box>
    );
};

export default StationPage;
